import React, {
	useState,
	useEffect,
	useReducer,
	Reducer,
	useContext,
} from 'react';
import {
	Grid,
	AppBar,
	Tabs,
	Tab,
	useMediaQuery,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	IconButton,
	TextField,
	ListItemSecondaryAction,
	Divider,
} from '@material-ui/core';
import { AddBox, Delete as DeleteIcon, Check } from '@material-ui/icons';
import theme from '@ifca-root/react-component/src/assets/theme';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import { RetailPOSSearchBar } from './RetailPOSSearchBar';
import { RetailPOSSubheader } from './RetailPOSSubheader';
import {
	RetailPOSAmountFooter,
	VoidRetailODDialog,
} from './RetailPOSAmountFooter';
import { RetailFooter } from './RetailFooter';
import { ShoppingCartIcon, NoImage, OutletIcon, RecommendedIcon } from 'assets';
import { amtStr } from 'helpers/numFormatter';
import './retail.scss';
import { CommonDialogV2 } from '@ifca-root/react-component/src/components/Dialog/CommonDialogV2';
import {
	CalcType,
	CreditCardType,
	GetAllOrderItemDocument,
	GetOrderDocument,
	GetOrderItemDocument,
	GetScheduledDailyMenuCategoryDocument,
	OrderDiscountStatus,
	OrderItemDiscountStatus,
	OrderItemStatus,
	OrderStatus,
	PatronClass,
	PaymentClass,
	ServiceMode,
	useAttachmentListingLazyQuery,
	useCreateOrderDiscountMutation,
	useCreatePaymentByQuantityMutation,
	useCreateRetailOrderItemWithOptionsMutation,
	useDeleteOrderDiscountMutation,
	useDeleteRetailOrderItemWithOptionsMutation,
	useGetAllOrderItemLazyQuery,
	useGetAssignedOutletByUserQuery,
	useGetCurrentMealPeriodLazyQuery,
	useGetDefaultPaymentQuery,
	useGetDiscountQuery,
	useGetFilteredDiscountLazyQuery,
	useGetOrderLazyQuery,
	useGetOutletDailyMenuItemByIdLazyQuery,
	useGetOutletNameQuery,
	useGetPatronQuery,
	useGetReasonLazyQuery,
	useGetRoundingPolicyQuery,
	useGetScheduledDailyMenuCategoryLazyQuery,
	useGetTableQuery,
	useGetUomQuery,
	useUpdateRetailOrderItemWithOptionsMutation,
} from 'generated/graphql';
import { useHistory, useLocation, useParams } from 'react-router';
import { IAction } from 'helpers/model';
import AppContext from 'containers/App/Store/AppContext';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import { useFuseSearch } from 'helpers/hooks/useSearch';
import { dateFormat } from 'helpers/formatDate';
import { formatTime } from 'helpers/hooks/formatTime';
import BigNumber from 'bignumber.js';
import { SearchHeaderRetail } from './SearchHeaderRetail';
import { RetailMenuDialogAdd } from 'containers/OutletModule/HomeNew/AddEditOrder/CustomDialog';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { SystemMsgs } from 'helpers/SystemMsg';
import PickupIcon from 'assets/icons/pickup_grey.svg';
import EditIcon from '@material-ui/icons/Edit';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { RetailMenuDialogSummary } from 'containers/OutletModule/HomeNew/OrderSummary/CustomDialog';
import { FooterAmount } from 'containers/OutletModule/HomeNew/SharedFunction';
import { Footer } from '@ifca-root/react-component/src/components/Footer/Footer';
import { RetailPaymentDialog } from './RetailPaymentDialog';

interface RetailPOSLayoutProps {
	// Main Header Props
	mainHeaderProps?: {
		mainBtn?: string;
		onClick?: () => void;
		smTitle?: string;
		title?: string;
		sideBtn?: string;
		onClickAction?: () => void;
		routeSegments?: any[];
		rightRouteSegments?: any[];
	};

	// Left Panel Props
	leftPanelProps?: {
		tabs?: any;
		searchBar?: {
			onChangeAction?: (event: React.ChangeEvent<HTMLInputElement>) => void;
			onCloseAction?: () => void;
			placeholder?: string;
		};
		children: any; // Scrollable content
	};

	// Right Panel Props
	rightPanelProps?: {
		subheader?: {
			title?: any;
			infoLine?: any;
		};
		children: any; // Scrollable content
		footer?: {
			amountFooter?: {
				currencyCode?: string;
				subtotal?: string;
				discount?: string;
				serviceTax?: string;
				total?: string;
				rounding?: string;
				page?: string;
			};
			retailFooter?: {
				options?: Array<{
					visible?: boolean;
					name: string;
					onClick?: () => void;
					color?: 'default' | 'primary' | 'secondary';
					disabled?: boolean;
					startIcon?: React.ReactNode;
				}>;
			};
		};
	};
}

interface Props {
	selectedOrder: any;
	outletID: any;
	orderID: any;
	tableID: any;
	currentMealPeriod: any;
	tabValue: {
		tab: any;
		categoryID: any;
	};
	currentMenu: any;
	selectedMenu: any;
	menuDialog: boolean;
	menuDialogUpdate: boolean;
	setMenuDialog: boolean;
	menuItemPatronPrice: any;
	quantity: any;
	weightName: any;
	takeAwayStatus: boolean;
	optionError: boolean;
	isSubmitting: boolean;
	paymentDialog: boolean;
	receivedAmount: number;
	changeAmount: number;
}

interface OptionItem {
	remark: string;
	name: string;
	optionItem: String;
	openPrice: number;
	specialRequest: String;
	option: any[];
	quantity: number;
}

interface OrderDiscountProps {
	discountCode: string;
	discountID: string;
}

interface VoidDiscountProps {
	reasonCode: string;
	remark: string;
}

function a11yProps(index: any) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

export const RetailSplitScreen: React.FC<RetailPOSLayoutProps> = ({
	mainHeaderProps,
	leftPanelProps,
	rightPanelProps,
}) => {
	const history = useHistory();
	let location = useLocation();

	const currTs = new Date();
	const currTimeInt = currTs.getHours() * 60 + currTs.getMinutes();

	const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
		defaultMatches: true,
	});

	const { globalState }: any = useContext(AppContext as any);

	const { setOpenSnackBar, setSnackBarMsg }: any = useContext(
		SnackBarContext as any,
	);

	const { tableID, outletID, orderID } = useParams<Record<string, any>>();

	// Drawer Toggler

	const handleDrawer = () => {
		dispatch({
			type: 'drawerOpen',
			payload: !globalState.drawerOpen,
		});
	};

	// state
	const initialState: Props = {
		selectedOrder: {},
		outletID: outletID,
		orderID: orderID,
		tableID: tableID,
		currentMealPeriod: null,
		tabValue: {
			tab: 0,
			categoryID: '',
		},
		currentMenu: {},
		selectedMenu: {},
		menuDialog: false,
		menuDialogUpdate: false,
		setMenuDialog: false,
		menuItemPatronPrice: 0,
		quantity: 1,
		weightName: null,
		takeAwayStatus: false,
		optionError: false,
		isSubmitting: false,
		paymentDialog: false,
		receivedAmount: 0,
		changeAmount: 0,
	};

	const reducer: Reducer<Props, IAction> = (state, action) => {
		switch (action.type) {
			case 'reset':
				return initialState;
			default:
				return { ...state, [action.type]: action.payload };
		}
	};

	// localStorage
	const menuSearch = JSON.parse(localStorage.getItem('searchFilter'));
	const user = JSON.parse(localStorage.getItem('loggedInUser'));

	// Default state for example/demo mode
	const [tabValue, setTabValue] = useState(0);
	const [searchValue, setSearchValue] = useState('');
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [state, dispatch] = useReducer(reducer, initialState);
	const [openSwitch, setOpenSwitch] = useState(null);
	const [currentMealPeriod, setCurrentMealPeriod] = useState(null);
	const [audioInput, setAudioInput] = useState('');
	const [record, setRecord] = useState(false);
	const [cartItems, setCartItems] = useState(null);
	const [locked, setLocked] = useState(false);
	const [locked2, setLocked2] = useState(false);
	const [selectedCredit, setSelectedCredit] = useState({} as any);

	// form
	const yupSchema = yup.object().shape({
		openItem: state?.currentMenu?.isOpenItem
			? yup
					.string()
					.required('Open item name is required')
					.trim()
			: null,
		openPrice:
			state?.currentMenu?.isOpenPrice || state?.currentMenu?.isOpenItem
				? yup.number().required('Open item price is required')
				: null,
	});

	let {
		handleSubmit,
		register,
		control,
		watch,
		setValue,
		unregister,
		errors,
		reset,
		clearErrors,
	}: any = useForm<OptionItem>({
		defaultValues: {},
		mode: 'onSubmit',
		resolver: yupResolver(yupSchema),
	});

	const {
		register: ODRegister,
		handleSubmit: ODHandleSubmit,
		control: ODControl,
	} = useForm<OrderDiscountProps>({
		defaultValues: {},
	});

	const {
		register: voidRegister,
		control: voidControl,
		handleSubmit: voidHandleSubmit,
		watch: voidWatch,
		errors: voidErrors,
	} = useForm<VoidDiscountProps>({
		defaultValues: {},
		mode: 'onSubmit',
	});

	let amount = 0;
	let totalAmountFooter = amtStr(state?.selectedOrder?.outstandingAmount);

	// API
	const {
		loading: outletLoading,
		data: { getOutlet } = { getOutlet: [] },
		refetch: outletRefetch,
		fetchMore: outletFetchMore,
	} = useGetOutletNameQuery({
		fetchPolicy: 'network-only',
		variables: { ID: state?.outletID },
		onCompleted: ({ getOutlet }) => {
			loadCurrentMealPeriod({
				variables: {
					outletID: state?.outletID,
				},
			});
			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});
			loadReason({
				variables: {
					outletID: outletID,
				},
			});
		},
	});

	const {
		data: { getAssignedOutletByUser } = { getAssignedOutletByUser: [] },
		loading: getAssignedOutletByUserLoading,
	} = useGetAssignedOutletByUserQuery({
		fetchPolicy: 'network-only',
		onCompleted: () => {
			loadCurrentMealPeriod({
				variables: {
					outletID: outletID,
				},
			});
			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});
			loadReason({
				variables: {
					outletID: outletID,
				},
			});
		},
	});

	let patronID;
	const {
		data: { getPatron } = { getPatron: [] },
		loading: patronLoading,
		refetch: patronRefetch,
		fetchMore: patronFetchMore,
	} = useGetPatronQuery({
		fetchPolicy: 'network-only',
		variables: { outletID: outletID, isActive: true },
		onCompleted: getPatron => {
			console?.log(getPatron, 'wwww');
			const visitorPatron = getPatron?.getPatron?.find(
				x => x?.patronClass === 'Visitor',
			);
			patronID = visitorPatron?.ID;
			loadCurrentMealPeriod({
				variables: {
					outletID: outletID,
				},
			});
			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});
		},
	});

	const [
		loadScheduledMenuCategory,
		{
			loading: scheduledDailyMenuCategoryLoading,
			data: { getOutletDailyMenuCategory } = { getOutletDailyMenuCategory: [] },
		},
	] = useGetScheduledDailyMenuCategoryLazyQuery({
		fetchPolicy: 'network-only',
	});

	const [
		loadCurrentMealPeriod,
		{
			loading: getCurrentMealPeriodLoading,
			data: { getCurrentMealPeriod } = { getCurrentMealPeriod: null },
		},
	] = useGetCurrentMealPeriodLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getCurrentMealPeriod }) => {
			setCurrentMealPeriod(getCurrentMealPeriod);

			dispatch({
				type: 'currentMealPeriod',
				payload: getCurrentMealPeriod,
			});
			loadScheduledMenuCategory({
				variables: {
					outletID: outletID,
					mealPeriodID: getCurrentMealPeriod?.ID,
				},
			});
		},
	});

	const [
		loadOrder,
		{ loading: orderLoading, data: { getOrder } = { getOrder: [] } },
	] = useGetOrderLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getOrder }) => {
			dispatch({
				type: 'selectedOrder',
				payload: getOrder?.filter(order => order?.ID === state?.orderID)[0],
			});

			// ✅ ONLY load order items if order ID exists
			if (getOrder[0]?.ID) {
				loadOrderItem({
					variables: {
						orderID: getOrder[0]?.ID,
						outletID,
					},
				});
			} else {
				// ✅ explicitly clear cart items
				setCartItems([]);
			}
		},
	});

	const [
		loadReason,
		{ loading: reasonLoading, data: { getReason } = { getReason: [] } },
	] = useGetReasonLazyQuery({
		fetchPolicy: 'network-only',
	});

	const [
		loadOutletDailyMenuItemByID,
		{
			loading: getOutletDailyMenuItemByIDLoading,
			data: { getOutletDailyMenuItemByID } = { getOutletDailyMenuItemByID: [] },
		},
	] = useGetOutletDailyMenuItemByIdLazyQuery({
		fetchPolicy: 'network-only',
	});

	const {
		loading: uomLoading,
		data: { getUOM } = { getUOM: [] },
	} = useGetUomQuery({
		fetchPolicy: 'network-only',
	});

	const [
		loadOrderItem,
		{
			loading: orderItemLoading,
			data: { getAllOrderItem } = { getAllOrderItem: [] },
		},
	] = useGetAllOrderItemLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getAllOrderItem }) => {
			setCartItems(getAllOrderItem);
			loadFilteredDiscount({
				variables: {
					outletID: outletID,
					amount: footerAmount.subtotal,
					isOrderItemDiscount: false,
				},
			});
		},
	});

	const [
		loadAttachment,
		{ loading: attachmentLoading, data: attachmentData },
	] = useAttachmentListingLazyQuery({
		fetchPolicy: 'network-only',
	});

	const [
		loadFilteredDiscount,
		{
			loading: getFilteredDiscountLoading,
			data: { getFilteredDiscount } = { getFilteredDiscount: [] },
		},
	] = useGetFilteredDiscountLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: () => {},
	});

	const {
		data: { getDiscount } = { getDiscount: [] },
		loading: discountLoading,
	} = useGetDiscountQuery({
		fetchPolicy: 'network-only',
		onCompleted: () => {},
	});

	const {
		data: { getRoundingPolicy } = { getRoundingPolicy: [] },
		loading: getRoundingPolicyLoading,
	} = useGetRoundingPolicyQuery({
		fetchPolicy: 'network-only',
		variables: { accountID: user?.accountID },
	});

	const footerAmount = FooterAmount(
		getAllOrderItem,
		'payment',
		getRoundingPolicy,
	);

	console?.log(footerAmount, 'footerAmount');

	// Handle & Function
	const {
		filteredList: filteredListOutlet,
		handleSearch: handleSearchOutlet,
		setOriginalListing: setOriginalListingOutlet,
		reset: resetOutlet,
	} = useFuseSearch();

	useEffect(() => {
		if (getAssignedOutletByUser && getAssignedOutletByUser?.length > 0) {
			setOriginalListingOutlet(getAssignedOutletByUser);
		}
	}, [getAssignedOutletByUser]);

	const {
		filteredList: filteredListOutletDailyMenuCategory,
		handleSearch: handleSearchOutletDailyMenuCategory,
		setOriginalListing: setOriginalListingOutletDailyMenuCategory,
	} = useFuseSearch();

	const categoryID =
		state?.tabValue?.categoryID === ''
			? getOutletDailyMenuCategory[0]?.ID
			: state?.tabValue?.categoryID;

	useEffect(() => {
		setSelectedCategory(
			getOutletDailyMenuCategory?.filter(
				category => category?.ID === categoryID,
			)[0],
		);

		setOriginalListingOutletDailyMenuCategory(
			getOutletDailyMenuCategory
				?.filter(category => category?.ID === categoryID)[0]
				?.outletDailyMenuItem?.filter(
					item => item?.menuItem?.stopSales === null,
				),
		);

		localStorage.setItem(
			'searchFilter',
			JSON.stringify({
				category: getOutletDailyMenuCategory?.filter(
					category => category?.ID === categoryID,
				)[0]?.ID,
				searchString: menuSearch?.searchString ?? '',
			}),
		);
	}, [getOutletDailyMenuCategory, categoryID]);

	useEffect(() => {
		if (
			!scheduledDailyMenuCategoryLoading &&
			selectedCategory?.outletDailyMenuItem?.length > 0 &&
			!!menuSearch?.searchString
		) {
			handleSearchOutletDailyMenuCategory(
				`'"${menuSearch?.searchString ?? ''}"`,
				['name'],
			);
		}
	}, [scheduledDailyMenuCategoryLoading, selectedCategory]);

	const handleChange = (event, newValue) => {
		const categoryID = getOutletDailyMenuCategory?.filter(
			(x, index) => index === newValue,
		)[0]?.ID;

		dispatch({
			type: 'tabValue',
			payload: {
				tab: newValue,
				categoryID: categoryID,
			},
		});

		localStorage.setItem(
			'searchFilter',
			JSON.stringify({
				searchString: menuSearch?.searchString ?? '',
				category: event?.ID,
			}),
		);
	};

	const checkSetMenu = setMenuOption => {
		let outstanding: boolean;
		setMenuOption?.map(x => {
			x?.setMenuOptionItem?.map(y => {
				if (y?.menuItem?.dailyAvailability) {
					if (y?.menuItem?.dailyAvailability?.outstandingQty <= 0) {
						outstanding = true;
					} else if (y?.menuItem?.dailyAvailability?.outstandingQty > 0) {
						outstanding = false;
					}
				}
			});
		});

		return outstanding;
	};

	const allRange = [];
	for (var i = 0; i <= 1440; i++) {
		allRange.push(i);
	}

	const getTimeRange = range => {
		if (range.startTime < range.endTime) {
			let timeRange = [];
			for (var i = range.startTime; i <= range.endTime; i++) {
				timeRange.push(i);
			}
			return timeRange;
		} else {
			let timeRange = [];
			for (var i = range.endTime + 1; i <= range.startTime; i++) {
				timeRange.push(i);
			}
			return allRange.filter(val => !timeRange.includes(val));
		}
	};

	const menuItemListPatronPrice = (item, promo?: Boolean) => {
		let price;
		console?.log(item, '123');
		if (!promo) {
			price = item?.outletDailyMenuItemPatronPrice?.filter(
				itemprice => itemprice?.patronClass === PatronClass.Visitor,
			)[0]?.price;
		} else {
			price = item?.outletDailyPromoMenuItem
				.filter(item => {
					const formattedExItem = {
						startTime:
							Number(item?.startTime.split(':')[0]) * 60 +
							Number(item?.startTime.split(':')[1]),
						endTime:
							Number(item?.endTime.split(':')[0]) * 60 +
							Number(item?.endTime.split(':')[1]),
					};
					//find if existing item overlap with current one
					if (getTimeRange(formattedExItem).includes(currTimeInt)) {
						return item;
					}
				})[0]
				?.outletDailyPromoMenuItemPrice?.filter(
					id => id?.patronClass === PatronClass.Visitor,
				)[0]?.price;
		}
		return price;
	};

	const checkItem = (item, mode) => {
		if (!item?.menuItem?.isSetMenu) {
			mode === 'ADD'
				? dispatch({
						type: 'menuDialog',
						payload: true,
				  })
				: dispatch({
						type: 'menuDialogUpdate',
						payload: true,
				  });
			console?.log(mode, 'mode');
		}
		setAudioInput(item?.specialRequest);
		setValue('specialRequest', item?.specialRequest);
	};

	const [
		createRetailOrderItemWithOptions,
		{ loading: createOrderItemWithOptionsLoading },
	] = useCreateRetailOrderItemWithOptionsMutation({
		onError: error => {
			dispatch({ type: 'optionError', payload: true });
			setOpenSnackBar(true);
			setSnackBarMsg(error.message);
			history.push(`/menu/outlet-app/${outletID}/retail`);
		},
		onCompleted: data => {
			dispatch({ type: 'menuDialog', payload: false });
			dispatch({ type: 'optionError', payload: false });
			dispatch({ type: 'isSubmitting', payload: false });
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.createNewRecord());
			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});
			dispatch({
				type: 'currentMenu',
				payload: null,
			});
		},
	});

	const [
		updateRetailOrderItemWithOptions,
		{ loading: updateOrderItemWithOptionsLoading },
	] = useUpdateRetailOrderItemWithOptionsMutation({
		onError: error => {
			dispatch({ type: 'optionError', payload: true });
			setOpenSnackBar(true);
			setSnackBarMsg(error.message);
			history.push(`/menu/outlet-app/${outletID}/retail`);
		},
		onCompleted: data => {
			dispatch({ type: 'menuDialogUpdate', payload: false });
			dispatch({ type: 'optionError', payload: false });
			dispatch({ type: 'isSubmitting', payload: false });
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.createNewRecord());
			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});
			dispatch({
				type: 'selectedMenu',
				payload: null,
			});
		},
	});

	const [
		deleteRetailOrderItemWithOptions,
		{ loading: deleteOrderItemWithOptionsLoading },
	] = useDeleteRetailOrderItemWithOptionsMutation({
		onError: error => {
			dispatch({ type: 'optionError', payload: true });
			setOpenSnackBar(true);
			setSnackBarMsg(error.message);
			history.push(`/menu/outlet-app/${outletID}/retail`);
		},
		onCompleted: data => {
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.deleteRecord());
			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});
		},
	});

	const {
		loading: paymentLoading,
		data: { getDefaultPayment } = { getDefaultPayment: [] },
	} = useGetDefaultPaymentQuery({
		fetchPolicy: 'network-only',
		variables: { outletID: state?.outletID, orderByAsc: 'paymentClass' },
	});

	const [
		createPaymentByQuantity,
		{ loading: paymentQuantityLoading },
	] = useCreatePaymentByQuantityMutation({
		onError: error => {
			console.log('ERROR', error?.message);
			setOpenSnackBar(true);
			setSnackBarMsg(error.message);
		},
		onCompleted: ({ paymentByQuantity }) => {
			history.push(
				`/menu/outlet-app/${outletID}/table/grid/${tableID}/order/${state?.selectedOrder?.ID}/bill-settlement/${paymentByQuantity?.ID}`,
			);
			dispatch({ type: 'radioError', payload: false });
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.createNewRecord());
			setLocked(false);
			setLocked2(false);
		},
	});

	useEffect(() => {
		dispatch({
			type: 'selectedOrder',
			payload: getOrder?.filter(order => order?.ID === state?.orderID)[0],
		});
	}, [getOrder?.length, state?.orderID]);

	useEffect(() => {
		if (state?.currentMenu?.promoChecking) {
			dispatch({
				type: 'menuItemPatronPrice',
				payload: new BigNumber(
					menuItemListPatronPrice(state?.currentMenu, true) ?? 0,
				).toNumber(),
			});
		} else {
			dispatch({
				type: 'menuItemPatronPrice',
				payload: new BigNumber(
					menuItemListPatronPrice(state?.currentMenu) ?? 0,
				).toNumber(),
			});
		}
	}, [state?.currentMenu]);

	useEffect(() => {
		loadAttachment({ variables: { refID: state?.currentMenu?.menuItem?.ID } });
	}, [state?.currentMenu]);

	useEffect(() => {
		dispatch({
			type: 'weightName',
			payload: getUOM?.filter(id => id?.ID === state?.currentMenu?.uomID)[0],
		});
	}, [getOutletDailyMenuItemByID[0]?.ID, state?.currentMealPeriod]);

	useEffect(() => {
		unregister(`option`);
		reset();
	}, [state?.menuDialog, state?.currentMenu]);

	useEffect(() => {
		if (state?.setMenuDialog) {
			state?.currentMenu?.menuItem?.setMenuOption
				?.filter(setMenuOption => setMenuOption?.maxSelections === 0)[0]
				?.setMenuOptionItem?.map((setMenuOptionItem, i) => {
					register(
						`option[0].${
							state?.currentMenu?.menuItem?.setMenuOption?.filter(
								setMenuOption => setMenuOption?.maxSelections === 0,
							)[0].ID
						}[${setMenuOptionItem?.ID}]`,
					);
					setValue(
						`option[0].${
							state?.currentMenu?.menuItem?.setMenuOption?.filter(
								setMenuOption => setMenuOption?.maxSelections === 0,
							)[0].ID
						}[${setMenuOptionItem?.ID}]`,
						{
							quantity: setMenuOptionItem?.quantity,
							menuItemID: setMenuOptionItem?.menuItem?.ID,
						},
					);
				});
		}
	}, [state?.setMenuDialog, state?.currentMenu]);

	const AddMenuOrderItem = async (data: any) => {
		if (data?.getWeight) {
			amount = state?.menuItemPatronPrice * parseFloat(data?.getWeight);
		} else if (data?.openPrice) {
			amount = parseFloat(data?.openPrice) * state?.quantity;
		} else {
			amount = state?.menuItemPatronPrice * state?.quantity;
		}

		createRetailOrderItemWithOptions({
			variables: {
				orderItemInput: {
					unitPrice: parseFloat(data?.openPrice) || state?.menuItemPatronPrice,
					name: state?.currentMenu?.name,
					description: state?.currentMenu?.description,
					outletID: outletID,
					amount: amount,
					baseAmount: amount,
					quantity: data?.getWeight ? 1 : state?.quantity,
					menuItemID: state?.currentMenu?.menuItemID,
					mealPeriodID: state?.currentMealPeriod?.ID,
					status: OrderItemStatus.Confirmed,
					specialRequest: audioInput,
					isTakeaway: state?.takeAwayStatus,
					openItemName: data?.openItem,
					weight: parseFloat(data?.getWeight) || 1,
					digitalMenuCategoryID:
						state?.tabValue?.categoryID === ''
							? getOutletDailyMenuCategory[0]?.digitalMenuCategoryID
							: getOutletDailyMenuCategory?.filter(
									x => x?.ID === state?.tabValue?.categoryID,
							  )[0]?.digitalMenuCategoryID,
				},
				orderItemOptionInput: data?.option?.map(orderItemOption => {
					let optionID = Object?.keys(orderItemOption)[0];
					return {
						outletID,
						optionID: optionID,
						orderItemOptionItems:
							typeof orderItemOption[optionID][0] === 'string'
								? [
										{
											outletID,
											optionItemID: JSON.parse(orderItemOption[optionID][0])
												?.itemID,
											price:
												JSON.parse(orderItemOption[optionID][0])?.price *
												state?.quantity,
											unitPrice: JSON.parse(orderItemOption[optionID][0])
												?.price,
										},
								  ]
								: Object.keys(orderItemOption[optionID])
										?.filter(item => item != null)
										?.map(optionItem => {
											return {
												outletID,
												optionItemID: optionItem,
												price:
													orderItemOption[optionID][optionItem] *
													state?.quantity,
												unitPrice: orderItemOption[optionID][optionItem],
											};
										}),
					};
				}),
			},
			refetchQueries: [
				{
					query: GetScheduledDailyMenuCategoryDocument,
					variables: {
						outletID: state?.outletID,
						mealPeriodID: state?.currentMealPeriod?.ID,
					},
				},
				// {
				// 	query: GetOrderDocument,
				// 	variables: {
				// 		tableID: state?.tableID,
				// 		status: OrderStatus.Open,
				// 		outletID,
				// 	},
				// },
			],
		});
		dispatch({ type: 'quantity', payload: 1 });
		dispatch({ type: 'takeAwayStatus', payload: false });
	};

	const UpdateMenuOrderItem = (data: any) => {
		if (data?.getWeight) {
			amount = parseFloat(data?.getWeight) * state?.selectedMenu?.unitPrice;
		} else if (data?.openPrice) {
			amount = parseFloat(data?.openPrice);
		} else {
			amount = state?.selectedMenu?.unitPrice * state?.quantity;
		}

		updateRetailOrderItemWithOptions({
			variables: {
				orderItemInput: {
					ID: state?.selectedMenu?.ID,
					outletID: outletID,
					amount: amount,
					baseAmount: amount,
					quantity: data?.getWeight ? 1 : state?.quantity,
					unitPrice:
						parseFloat(data?.openPrice) || state?.selectedMenu?.unitPrice,
					menuItemID: state?.selectedMenu?.menuItemID,
					orderID: orderID,
					mealPeriodID: state?.currentMealPeriod?.ID,
					status: OrderItemStatus.Confirmed,
					specialRequest: audioInput,
					isTakeaway: state?.takeAwayStatus,
					openItemName: data?.openItem || state?.selectedMenu?.openItemName,
					weight: parseFloat(data?.getWeight) || state?.selectedMenu?.weight,
				},
				orderItemOptionInput: data?.option?.map(orderItemOption => {
					let optionID = Object.keys(orderItemOption)[0];
					return {
						outletID,
						optionID: optionID,
						orderItemOptionItems:
							typeof orderItemOption[optionID][0] === 'string'
								? [
										{
											outletID,
											optionItemID: JSON.parse(orderItemOption[optionID][0])
												?.itemID,
											price:
												JSON.parse(orderItemOption[optionID][0])?.price *
												state?.quantity,
											basePrice:
												JSON.parse(orderItemOption[optionID][0])?.price *
												state?.quantity,
										},
								  ]
								: Object.keys(orderItemOption[optionID])
										?.filter(item => item !== null)
										?.map(optionItem => {
											return {
												outletID,
												optionItemID: optionItem,
												price:
													orderItemOption[optionID][optionItem] *
													state?.quantity,
												basePrice:
													orderItemOption[optionID][optionItem] *
													state?.quantity,
											};
										}),
					};
				}),
			},
			refetchQueries: [
				{
					query: GetScheduledDailyMenuCategoryDocument,
					variables: {
						outletID: state?.outletID,
						mealPeriodID: state?.currentMealPeriod?.ID,
					},
				},
				// {
				// 	query: GetOrderDocument,
				// 	variables: {
				// 		tableID: state?.tableID,
				// 		status: OrderStatus.Open,
				// 		outletID,
				// 	},
				// },
			],
		});
		dispatch({ type: 'quantity', payload: state?.selectedMenu?.quantity });
		dispatch({ type: 'takeAwayStatus', payload: false });
	};

	// Cart handlers
	const handleRemoveItem = (data: any) => {
		console?.log(data, 'VA');
		deleteRetailOrderItemWithOptions({
			variables: {
				orderItemInput: {
					ID: data?.ID,
					unitPrice: parseFloat(data?.openPrice) || state?.menuItemPatronPrice,
					name: state?.currentMenu?.name,
					description: state?.currentMenu?.description,
					outletID: state?.outletID,
					amount: amount,
					baseAmount: amount,
					quantity: 0,
					menuItemID: data.menuItemID,
					mealPeriodID: state?.currentMealPeriod?.ID,
					status: OrderItemStatus.Confirmed,
					specialRequest: audioInput,
					isTakeaway: state?.takeAwayStatus,
					openItemName: data?.openItem,
					weight: parseFloat(data?.getWeight) || 1,
					digitalMenuCategoryID:
						state?.tabValue?.categoryID === ''
							? getOutletDailyMenuCategory[0]?.digitalMenuCategoryID
							: getOutletDailyMenuCategory?.filter(
									x => x?.ID === state?.tabValue?.categoryID,
							  )[0]?.digitalMenuCategoryID,
				},
				orderItemOptionInput: data?.option?.map(orderItemOption => {
					let optionID = Object?.keys(orderItemOption)[0];
					return {
						outletID,
						optionID: optionID,
						orderItemOptionItems:
							typeof orderItemOption[optionID][0] === 'string'
								? [
										{
											outletID,
											optionItemID: JSON.parse(orderItemOption[optionID][0])
												?.itemID,
											price:
												JSON.parse(orderItemOption[optionID][0])?.price *
												state?.quantity,
											unitPrice: JSON.parse(orderItemOption[optionID][0])
												?.price,
										},
								  ]
								: Object.keys(orderItemOption[optionID])
										?.filter(item => item != null)
										?.map(optionItem => {
											return {
												outletID,
												optionItemID: optionItem,
												price:
													orderItemOption[optionID][optionItem] *
													state?.quantity,
												unitPrice: orderItemOption[optionID][optionItem],
											};
										}),
					};
				}),
			},
			refetchQueries: [
				{
					query: GetScheduledDailyMenuCategoryDocument,
					variables: {
						outletID: state?.outletID,
						mealPeriodID: state?.currentMealPeriod?.ID,
					},
				},
				// {
				// 	query: GetOrderDocument,
				// 	variables: {
				// 		tableID: state?.tableID,
				// 		status: OrderStatus.Open,
				// 		outletID,
				// 	},
				// },
			],
		});
		// setCartItems(prev => prev.filter(item => item.id !== itemId));
	};

	const totalItems = cartItems?.reduce((sum, item) => sum + item.quantity, 0);
	const totalAmount = cartItems?.reduce(
		(sum, item) => sum + item.price * item.quantity,
		0,
	);

	const creditCardEnum = data => {
		let credClass;
		if (data === 'American Express Card') {
			credClass = CreditCardType.AmericanExpressCard;
		} else if (data === 'Diners Card') {
			credClass = CreditCardType.DinersCard;
		} else if (data === 'JCB Card') {
			credClass = CreditCardType.JcbCard;
		} else if (data === 'Master Card') {
			credClass = CreditCardType.Mastercard;
		} else if (data === 'UNION PAY') {
			credClass = CreditCardType.UnionpayCard;
		} else if (data === 'Visa Card') {
			credClass = CreditCardType.VisaCard;
		} else if (data === 'Debit Card') {
			credClass = CreditCardType.DebitCard;
		} else if (data === 'E-Wallet') {
			credClass = CreditCardType.Ewallet;
		}
		return credClass;
	};

	const selectedPaymentClass = () => {
		const name = getDefaultPayment?.filter(
			x => x?.ID === watch('defaultPaymentID'),
		)[0]?.paymentClass;
		return name;
	};

	const getSelectedOrderItemIDs = () => {
		let temp = getAllOrderItem?.filter(v => v?.quantity > 0);

		return temp?.map(x => {
			return {
				orderItemID: x?.ID,
				paymentQuantity: x?.quantity,
			};
		});
	};

	let cardType: any = watch('cardType');
	let paymentInputArr = [];
	const onSubmit = async (data: any, files?) => {
		setLocked(true);
		const ref = state?.selectedOrder?.docNo;
		let billInput;
		let totalReceiveAmount = 0;

		console?.log(data, 'data1');

		const selectedPayments = data?.defaultPayment
			?.slice() // optional: create a shallow copy to avoid mutating the original
			?.sort((a, b) => (a?.paymentSort ?? 0) - (b?.paymentSort ?? 0))
			?.filter(x => x?.selected === true);
		const lastIndex = selectedPayments?.length - 1;

		selectedPayments
			?.slice() // optional: create a shallow copy to avoid mutating the original
			?.sort((a, b) => (a?.paymentSort ?? 0) - (b?.paymentSort ?? 0))
			?.map((v, index) => {
				billInput = {
					orderID: getOrder[0]?.ID,
					outletID: outletID,
				};

				let paymentInput = {
					outletID: outletID,
					amount:
						v?.paymentClass === PaymentClass?.Cash
							? state?.receivedAmount
							: (v?.amountPayment ?? 0) + (v?.rounding ?? 0),
					creditCardType:
						cardType?.GuestType !== null && cardType?.GuestType !== undefined
							? creditCardEnum(cardType?.GuestType)
							: cardType,
					cardNo: v?.cardRef,
					changeAmount:
						v?.paymentClass === PaymentClass?.Cash
							? Math.abs(state?.changeAmount) >= 0
								? Math.round(
										(Math.abs(state?.changeAmount ?? 0) + Number.EPSILON) * 100,
								  ) / 100
								: Math.round(
										(Math.abs(state?.changeAmount ?? 0) + Number.EPSILON) * 100,
								  ) / 100
							: 0,
					receivedAmount:
						v?.paymentClass === PaymentClass?.Cash
							? state?.receivedAmount !== 0
								? state?.receivedAmount ?? 0
								: (v?.amountPayment ?? 0) + (v?.rounding ?? 0)
							: (v?.amountPayment ?? 0) + (v?.rounding ?? 0),
					roundingAmount: index === lastIndex ? v?.rounding ?? 0 : 0,
					// saveRounding(data?.defaultPayment, v, index, lastIndex) ?? 0,
					defaultPaymentID: v?.defaultPaymentID,
					paymentClass: v?.paymentClass,
					patronAccountID: null,
					registrationID: null,
					referenceNo: v?.referenceNo,
					remark: v?.remark,
					billLedgerInput: {
						HotelID: null,
						RegistrationID: null,
						OutletCode: getOutlet[0]?.outletCode,
						IncidentalChargeID: '',
						Reason: '',
						Description: `${getOutlet[0]?.outletCode} -  ${ref}`,
						TrxDate: new Date(),
						Qty: 1,
						UnitPrice: 0,
						TrxAmount: 0,
						BaseAmount: 0,
						ServiceCharge: 0,
						TaxAmount: 0,
						DiscountAmount: 0,
						IsChargeToRoom: true,
						ReferenceNo: ref,
						CreatedBy: user?.ID,
					},
				};

				paymentInputArr?.push(paymentInput);
			});

		paymentInputArr?.map(x => {
			totalReceiveAmount += Number(
				x?.paymentClass === PaymentClass.Cash ? x?.receivedAmount : x?.amount,
			);
		});

		console.log('paymentInputArr >>>', paymentInputArr);

		dispatch({ type: 'lastArray', payload: paymentInputArr });

		// setBillInput(billInput);
		// setPaymentInput(paymentInput);

		console?.log(locked, locked2, 'locked');

		if (locked === false && locked2 === true) {
			createPaymentByQuantity({
				variables: {
					paymentByQuantityInput: getSelectedOrderItemIDs(),
					billInput,
					paymentInput: paymentInputArr,
				},
				// refetchQueries: [
				// 	{
				// 		query: GetOrderItemDocument,
				// 		variables: {
				// 			orderID: state?.selectedOrder?.ID,
				// 			status: OrderItemStatus.Confirmed,
				// 		},
				// 	},
				// 	{
				// 		query: GetAllOrderItemDocument,
				// 		variables: { orderID: state?.selectedOrder?.ID },
				// 	},
				// ],
			});
		}
	};

	const discountable = getAllOrderItem?.filter(
		x => x?.menuItem?.isDiscountable === true,
	);

	const [
		createOrderDiscount,
		{ data: createdOrderDiscount, loading: createdOrderDiscountLoading },
	] = useCreateOrderDiscountMutation({
		onError: error => {
			console.log('ERROR', error);
		},
		onCompleted: data => {
			dispatch({ type: 'isButtonClicked', payload: false });
			setOpenSnackBar(true);
			dispatch({
				type: 'selectedOrder',
				payload: getOrder?.filter(order => order?.ID === state?.orderID)[0],
			});

			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});

			if (discountable?.length === 0 && data?.createOrderDiscount === false) {
				setSnackBarMsg('Discount not applicable');
			} else {
				setSnackBarMsg(SystemMsgs.createNewRecord());
			}
		},
	});

	const onSubmitOD = (data: any) => {
		createOrderDiscount({
			variables: {
				input: {
					outletID: outletID,
					discountID: data?.discountCode?.ID,
					orderID: getOrder[0]?.ID,
					businessDate: getOutlet[0]?.businessDate,
					status: OrderDiscountStatus.Active,
					amount:
						getDiscount?.filter(
							discount => discount?.ID === data?.discountCode?.ID,
						)[0]?.calcType === CalcType.Amount
							? getDiscount?.filter(
									discount => discount?.ID === data?.discountCode?.ID,
							  )[0]?.amount
							: (footerAmount?.total *
									getDiscount?.filter(
										discount => discount?.ID === data?.discountCode?.ID,
									)[0]?.amount) /
							  100,
				},
			},
			refetchQueries: [
				{
					query: GetScheduledDailyMenuCategoryDocument,
					variables: {
						outletID: state?.outletID,
						mealPeriodID: state?.currentMealPeriod?.ID,
					},
				},
				// {
				// 	query: GetOrderDocument,
				// 	variables: {
				// 		tableID: state?.tableID,
				// 		status: OrderStatus.Open,
				// 		outletID,
				// 	},
				// },
			],
		});

		dispatch({ type: 'ODDialog', payload: false });
	};

	const [
		deleteOrderDiscount,
		{ loading: deleteOrderDiscountLoading },
	] = useDeleteOrderDiscountMutation({
		onError: error => {
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.voidRecordFail());
		},
		onCompleted: data => {
			dispatch({ type: 'isButtonClicked', payload: false });
			dispatch({
				type: 'selectedOrder',
				payload: getOrder?.filter(order => order?.ID === state?.orderID)[0],
			});

			loadOrder({
				variables: {
					status: OrderStatus.Open,
					outletID: outletID,
					serviceMode: ServiceMode.Retail,
				},
			});

			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.voidRecord());
		},
	});

	const onSubmitVoidOD = data => {
		deleteOrderDiscount({
			variables: {
				input: {
					orderID: getOrder[0]?.ID,
					outletID: outletID,
					remark: data?.remark,
					reasonCode: data?.reasonCode,
				},
			},
			refetchQueries: [
				{
					query: GetScheduledDailyMenuCategoryDocument,
					variables: {
						outletID: state?.outletID,
						mealPeriodID: state?.currentMealPeriod?.ID,
					},
				},
				// {
				// 	query: GetOrderDocument,
				// 	variables: {
				// 		tableID: state?.tableID,
				// 		status: OrderStatus.Open,
				// 		outletID,
				// 	},
				// },
			],
		});

		dispatch({ type: 'voidDialog', payload: false });
	};

	// Use provided props or defaults
	const finalMainHeaderProps =
		mainHeaderProps ||
		({
			mainBtn: 'menu',
			onClick: handleDrawer,
			smTitle: 'Outlet App',
			title: getAssignedOutletByUser?.filter(
				outlet => outlet?.ID === outletID,
			)[0]?.name,
			sideBtn: 'switch',
			onClickAction: () => setOpenSwitch(true),
			routeSegments: [{ name: 'Table Layout', current: true }],
			rightRouteSegments: [
				{
					name: currentMealPeriod
						? `${dateFormat(
								getAssignedOutletByUser?.filter(
									outlet => outlet?.ID === outletID,
								)[0]?.businessDate,
						  )} ${currentMealPeriod?.name} ${formatTime(
								currentMealPeriod?.startTime,
						  )} - ${formatTime(currentMealPeriod?.endTime)}`
						: `No applicable meal period at the moment`,
					current: true,
				},
			],
		} as any);

	const finalLeftPanelProps = leftPanelProps || {
		tabs: (
			<>
				<div className="retail-pos-tabs-container">
					<AppBar position="static" style={{ boxShadow: 'none' }}>
						<Tabs
							aria-label="category tabs"
							indicatorColor="primary"
							textColor="primary"
							value={state?.tabValue?.tab}
							onChange={handleChange}
							variant={'scrollable'}
							scrollButtons="auto"
						>
							{getOutletDailyMenuCategory
								.sort((a, b) => Number(a.priority) - Number(b.priority))
								?.map((x, index) => (
									<Tab label={x?.name} {...a11yProps(index)} />
								))}
						</Tabs>
					</AppBar>
				</div>
			</>
		),
		searchBar: (
			<>
				<div className="retail-pos-search-container">
					<SearchHeaderRetail
						datePickerSubInfo
						title="Search here"
						value={`${filteredListOutletDailyMenuCategory?.filter(e => {
							if (selectedCategory?.outletDailyMenuItem) {
								return e;
							}
						})?.length ?? '0'}`}
						onChangeAction={e => {
							handleSearchOutletDailyMenuCategory(`'"${e?.target?.value}"`, [
								'name',
							]);
							localStorage.setItem(
								'searchFilter',
								JSON.stringify({
									category: menuSearch?.category,
									searchString: e?.target?.value,
								}),
							);
						}}
						fixed
						search
						onCloseAction={() => {
							localStorage.setItem(
								'searchFilter',
								JSON.stringify({
									category: menuSearch?.category ?? '',
									searchString: '',
								}),
							);
							handleSearchOutletDailyMenuCategory('', []);
						}}
						clickText
					/>
				</div>
			</>
		),
		children: (
			<>
				<List className="core-list" style={{ marginBottom: '0px' }}>
					{filteredListOutletDailyMenuCategory?.map(el => {
						const notAvailable =
							el?.menuItem?.stopSales ||
							(el?.menuItem?.dailyAvailability &&
								el?.menuItem?.dailyAvailability?.outstandingQty <= 0) ||
							checkSetMenu(el?.menuItem?.setMenuOption);

						return (
							<>
								<ListItem
									style={{
										padding: '0px 10px 0px',
										backgroundColor: notAvailable ? '#ffc0c0' : null,
									}}
									disabled={notAvailable ? true : false}
								>
									<div
										className=""
										style={{
											paddingRight: '6px',
										}}
									>
										<ListItemIcon>
											{el?.attachments?.fileURL ? (
												<>
													<img
														className="img-owned"
														src={el?.attachments?.fileURL}
													/>
												</>
											) : (
												<img
													className="img-owned"
													style={{ border: 'none' }}
													src={NoImage}
												/>
											)}
										</ListItemIcon>
										{notAvailable && (
											<div className="retail-img-sold-out-text">SOLD</div>
										)}
										{el?.promoChecking && (
											<div className="retail-img-promo-text"> PROMO</div>
										)}
									</div>

									<ListItemText
										primary={
											<>
												{el?.isOpenPrice || el?.isOpenItem ? (
													<span className="xsTitle">
														{el?.isOpenPrice == true ? (
															<span
																className="xsTitle"
																style={{
																	overflowWrap: 'break-word',
																	whiteSpace: 'normal',
																	color: '#000',
																}}
															>
																{el?.name} (OP)
															</span>
														) : (
															<span
																className="xsTitle"
																style={{
																	overflowWrap: 'break-word',
																	whiteSpace: 'normal',
																	color: '#000',
																}}
															>
																{el?.name} (OI)
															</span>
														)}
													</span>
												) : (
													<span
														className="xsTitle"
														style={{
															overflowWrap: 'break-word',
															whiteSpace: 'normal',
															color: '#000',
														}}
													>
														{el?.name}
													</span>
												)}
												{el?.isChefRecommended && (
													<img
														className="icon-svg"
														src={RecommendedIcon}
														style={{
															height: '1em',
															marginLeft: '4px',
															marginBottom: '4px',
														}}
													/>
												)}
											</>
										}
										secondary={
											<>
												<span className="flex-space desc">
													<div>{el?.description}</div>
													<div>
														{el?.isByWeight ? (
															<span className="highlight-text">{`${getOutlet[0]
																?.currencyCode ?? ''}${amtStr(
																new BigNumber(
																	menuItemListPatronPrice(el),
																).toNumber(),
															)}/${
																getUOM?.filter(id => id?.ID === el?.uomID)[0]
																	?.code
															}`}</span>
														) : el?.promoChecking ? (
															<>
																<span className="highlight-text">
																	{`${getOutlet[0]?.currencyCode ?? ''}${amtStr(
																		new BigNumber(
																			menuItemListPatronPrice(el, true),
																		).toNumber(),
																	)}`}
																</span>
																<span
																	style={{
																		marginLeft: '8px',
																		textDecoration: 'line-through',
																		fontWeight: 'bolder',
																	}}
																>
																	{`${getOutlet[0]?.currencyCode ?? ''}${amtStr(
																		new BigNumber(
																			menuItemListPatronPrice(el),
																		).toNumber(),
																	)}`}
																</span>
															</>
														) : (
															<span className="highlight-text">
																{getOutlet[0]?.currencyCode ?? ''}
																{amtStr(
																	new BigNumber(
																		menuItemListPatronPrice(el),
																	).toNumber(),
																)}
															</span>
														)}
													</div>
												</span>
											</>
										}
									/>

									<IconButton
										style={{ padding: '0px' }}
										disabled={notAvailable ? true : false}
									>
										<AddBox
											style={{
												fontSize: '32px',
												paddingTop: '28px',
												color: '#ff9800',
											}}
											onClick={() => {
												dispatch({
													type: 'currentMenu',
													payload: el,
												});
												checkItem(el, 'ADD');
											}}
										/>
									</IconButton>
								</ListItem>
							</>
						);
					})}
				</List>
			</>
		),
	};

	console?.log(cartItems, 'TA');

	const finalRightPanelProps = rightPanelProps || {
		subheader: {
			title: (
				<span className="xsTitle color-primary-orange">Order Summary</span>
			),
			infoLine: (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
					}}
				>
					<span className="desc">
						{getOrder?.length > 0 ? getOrder[0]?.docNo : 'NA'}
					</span>
					<span className="desc right-info">Total Item: {totalItems}</span>
				</div>
			),
		},
		children: (
			<>
				{cartItems?.length === 0 ? (
					<List
						style={{
							backgroundColor: 'white',
							color: 'black',
							width: '100%',
							marginTop: '-70px',
							zIndex: 0,
						}}
					>
						<div className="empty-listing">
							<ListItemIcon>
								<img src={ShoppingCartIcon} alt="Empty cart" />
							</ListItemIcon>
							<div className="smTitle">No Item in the cart</div>
							<div className="desc">Add on item manually or</div>
							<div className="desc">scan a barcode to continue</div>
						</div>
					</List>
				) : (
					<List className="retail-pos-order-items">
						<>
							{cartItems?.map(item => {
								// =======================
								// Helpers (safe utilities)
								// =======================
								const safeNum = v => (Number.isFinite(v) ? v : 0);

								const sumTax = (taxList = []) =>
									Array.isArray(taxList)
										? taxList.reduce((sum, t) => sum + safeNum(t?.taxAmount), 0)
										: 0;

								// =======================
								// Item discount existence
								// =======================
								const hasItemDisc =
									(Array.isArray(item?.orderItemDiscount) &&
										item.orderItemDiscount.some(
											disc => disc?.status === OrderItemDiscountStatus.Active,
										)) ||
									safeNum(item?.orderDiscountItem?.amount) !== 0 ||
									(safeNum(item?.orderDiscountItem?.baseAmount) > 0 &&
										safeNum(item?.orderDiscountItem?.amount) === 0);

								// =======================
								// Item base + tax
								// =======================
								const baseItemAmountBill =
									safeNum(item?.amount) + sumTax(item?.orderItemTax);

								// =======================
								// Order Item Discount (OID)
								// =======================
								const activeOID = Array.isArray(item?.orderItemDiscount)
									? item.orderItemDiscount.find(
											disc => disc?.status === OrderItemDiscountStatus.Active,
									  )
									: null;

								const oid =
									safeNum(activeOID?.baseAmount) +
									sumTax(activeOID?.orderItemDiscountTax);

								// =======================
								// Order Discount Item (ODI)
								// =======================
								const odi =
									safeNum(item?.orderDiscountItem?.baseAmount) +
									sumTax(item?.orderDiscountItemTax);

								// =======================
								// Final discount (OID takes priority)
								// =======================
								const discount = oid > 0 ? oid : odi;

								// =======================
								// Option totals (amount & discount)
								// =======================
								const optionTotals = Array.isArray(item?.orderItemOption)
									? item.orderItemOption.reduce(
											(acc, oio) => {
												if (!Array.isArray(oio?.orderItemOptionItem))
													return acc;

												oio.orderItemOptionItem.forEach(oioptitem => {
													// ---- base + tax ----
													acc.amount +=
														safeNum(oioptitem?.basePrice) +
														sumTax(oioptitem?.orderItemOptionItemTax);

													// ---- discount + tax ----
													acc.discountAmount +=
														safeNum(oioptitem?.discountBaseAmount) +
														sumTax(oioptitem?.orderItemOptionItemDiscountTax) +
														sumTax(oioptitem?.orderDiscountItemOptionTax);
												});

												return acc;
											},
											{ amount: 0, discountAmount: 0 },
									  )
									: { amount: 0, discountAmount: 0 };

								// =======================
								// Final net amounts (optional)
								// =======================
								const netItemAmount =
									baseItemAmountBill +
									optionTotals.amount -
									discount -
									optionTotals.discountAmount;

								console?.log(optionTotals, 'optionList');

								return (
									<ListItem
										key={item.id}
										className="retail-pos-order-item"
										style={{ padding: '12px 16px' }}
									>
										<div style={{ paddingRight: '12px' }}>
											<ListItemIcon style={{ minWidth: '60px' }}>
												{/* <img
											className="img-owned"
											style={{
												width: '60px',
												height: '60px',
												objectFit: 'cover',
												borderRadius: '4px',
												border: 'none',
											}}
											src={item.image || NoImage}
											alt={item.name}
										/> */}
												{item?.menuItem?.attachments?.fileURL ? (
													<img
														className="img-owned"
														src={item?.menuItem?.attachments?.fileURL}
													/>
												) : (
													<img
														className="img-owned"
														style={{ border: 'none' }}
														src={NoImage}
													/>
												)}
												{item?.isTakeaway && (
													<img className="img-takeaway-logo" src={PickupIcon} />
												)}
											</ListItemIcon>
										</div>
										<ListItemText
											style={{ flex: 1 }}
											primary={
												<>
													<span className="xsTitle flex-space">
														{item?.menuItem?.name} x{item.quantity}
													</span>
												</>
											}
											secondary={
												<span className="flex-space desc">
													<div
														style={{
															overflowWrap: 'break-word',
															whiteSpace: 'normal',
														}}
													>
														{item?.menuItem?.description}
													</div>
													<div>
														{item?.orderItemOption?.map(x =>
															x?.orderItemOptionItem?.map(x => (
																<div>{x?.optionItem?.name}</div>
															)),
														)}
													</div>
													<div>{item?.specialRequest}</div>
												</span>
											}
										/>
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'flex-end',
												gap: '8px',
											}}
										>
											<div
												style={{
													textAlign: 'right',
												}}
											>
												{hasItemDisc && (
													<span
														className="mdDesc xsTitle right-info"
														style={{
															textDecoration: 'line-through ',
															padding: '7px 0px 4px 4px',
														}}
													>
														{`${getOutlet[0]?.currencyCode}${amtStr(
															Math.abs(
																new BigNumber(
																	baseItemAmountBill + optionTotals?.amount,
																).toNumber(),
															),
														)}`}
													</span>
												)}
												<span
													className="highlight-text flex-space xsTitle right-info"
													style={{
														color: '#ff9800',
														// fontWeight: 600,
														// fontSize: '14px',
														padding: '7px 0px 4px 4px',
													}}
												>
													{getOutlet[0]?.currencyCode ?? ''}
													{amtStr(
														Math.abs(
															new BigNumber(
																baseItemAmountBill -
																	discount +
																	optionTotals?.amount -
																	optionTotals?.discountAmount,
															).toNumber(),
														),
													)}
												</span>
											</div>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px',
												}}
											>
												<IconButton
													className="retail-pos-qty-btn retail-pos-qty-plus"
													onClick={() => {
														checkItem(item, 'UPDATE');
														dispatch({
															type: 'selectedMenu',
															payload: { ...item },
														});
														dispatch({
															type: 'quantity',
															payload: item?.quantity,
														});
														dispatch({
															type: 'takeAwayStatus',
															payload: item?.isTakeaway,
														});
													}}
												>
													<EditIcon />
												</IconButton>
												<IconButton
													className="retail-pos-qty-btn retail-pos-qty-delete"
													onClick={() => handleRemoveItem(item)}
												>
													<DeleteIcon style={{ fontSize: '16px' }} />
												</IconButton>
											</div>
										</div>
									</ListItem>
								);
							})}
						</>
					</List>
				)}
			</>
		),
		footer: {
			amountFooter: {
				currencyCode: getOutlet[0]?.currencyCode ?? '',
				subtotal: amtStr(footerAmount.subtotal),
				discount: amtStr(footerAmount.discount),
				serviceTax: amtStr(footerAmount.tax),
				total: amtStr(footerAmount.total),
				rounding: amtStr(footerAmount.roundingAmount),
				page: 'payment',
			},
			retailFooter: {
				options: [
					{
						visible: true,
						name: 'Print',
						onClick: () => console.log('Print clicked'),
						color: 'primary' as const,
						disabled: true,
					},
					{
						visible: true,
						name: 'Payment',
						onClick: e => {
							dispatch({ type: 'paymentDialog', payload: true });
							setLocked2(true);
						},
						color:
							footerAmount?.total === 0 || getAllOrderItem?.length === 0
								? '#faf2e8'
								: 'primary',
						disabled:
							footerAmount?.total === 0 || getAllOrderItem?.length === 0,
					},
				],
			},
		},
	};

	return (
		<>
			{outletLoading && <Loading />}
			{getCurrentMealPeriodLoading && <Loading />}
			{orderLoading && <Loading />}
			{orderItemLoading && <Loading />}
			{uomLoading && <Loading />}
			{deleteOrderItemWithOptionsLoading && <Loading />}
			{createOrderItemWithOptionsLoading && <Loading />}
			{patronLoading && <Loading />}
			{createdOrderDiscountLoading && <Loading />}
			{deleteOrderDiscountLoading && <Loading />}

			{/* Main Header */}
			<MainHeader
				mainBtn={finalMainHeaderProps.mainBtn}
				onClick={finalMainHeaderProps.onClick}
				smTitle={finalMainHeaderProps.smTitle}
				title={finalMainHeaderProps.title}
				sideBtn={finalMainHeaderProps.sideBtn}
				onClickAction={finalMainHeaderProps.onClickAction}
				routeSegments={finalMainHeaderProps.routeSegments}
				rightRouteSegments={finalMainHeaderProps.rightRouteSegments}
			/>

			{/* Main Container with Grid */}
			<div className="retail-pos-container">
				<Grid container className="retail-pos-grid-container">
					{/* Left Panel - 7 columns */}
					<Grid
						item
						xs={12}
						sm={7}
						className="retail-pos-left-panel"
						style={{ padding: 0 }}
					>
						{/* Fixed Header: Tabs + Search Bar */}
						<div
							className="retail-pos-left-header"
							style={{
								width:
									isDesktop && globalState.drawerOpen
										? 'calc((100% - 288px) * 7 / 12)'
										: 'calc((100% - 48px) * 7 / 12)',
							}}
						>
							{finalLeftPanelProps.tabs}

							{/* Search Bar */}
							{finalLeftPanelProps.searchBar}
						</div>

						{/* Scrollable Content */}
						<div
							className="retail-pos-left-content"
							style={{ marginTop: '90px', marginBottom: '2px' }}
						>
							{finalLeftPanelProps.children}
						</div>
					</Grid>

					{/* Right Panel - 5 columns */}
					<Grid
						item
						xs={12}
						sm={5}
						className="retail-pos-right-panel"
						style={{ padding: 0 }}
					>
						{/* Fixed Subheader */}
						{finalRightPanelProps.subheader && (
							<div className="retail-pos-right-header">
								<RetailPOSSubheader
									title={finalRightPanelProps.subheader.title}
									infoLine={finalRightPanelProps.subheader.infoLine}
								/>
							</div>
						)}

						{/* Scrollable Content */}
						<div className="retail-pos-right-content">
							{finalRightPanelProps.children}
						</div>

						{/* Fixed Footer */}
						{finalRightPanelProps.footer && (
							<div className="retail-pos-right-footer">
								{finalRightPanelProps.footer.amountFooter && (
									<RetailPOSAmountFooter
										currencyCode={
											finalRightPanelProps.footer.amountFooter.currencyCode ||
											''
										}
										subtotal={
											finalRightPanelProps.footer.amountFooter.subtotal || '0'
										}
										discount={
											finalRightPanelProps.footer.amountFooter.discount || '0'
										}
										serviceTax={
											finalRightPanelProps.footer.amountFooter.serviceTax || '0'
										}
										total={
											finalRightPanelProps.footer.amountFooter.total || '0'
										}
										rounding={
											finalRightPanelProps.footer.amountFooter.rounding || '0'
										}
										page={
											finalRightPanelProps.footer.amountFooter.page || 'payment'
										}
										getFilteredDiscount={getFilteredDiscount}
										outletID={outletID}
										getOrder={getOrder}
										register={ODRegister}
										control={ODControl}
										handleSubmit={ODHandleSubmit}
										onSubmitOD={onSubmitOD}
										voidRegister={voidRegister}
										voidControl={voidControl}
										voidErrors={voidErrors}
										voidWatch={voidWatch}
										voidHandleSubmit={voidHandleSubmit}
										getReason={getReason}
										onSubmitVoid={onSubmitVoidOD}
										dispatch={dispatch}
									/>
								)}
								{finalRightPanelProps.footer.retailFooter && (
									// <RetailFooter
									// 	options={
									// 		finalRightPanelProps.footer.retailFooter.options?.filter(
									// 			x => x?.visible !== false,
									// 		) || []
									// 	}
									// />
									<Footer
										options={
											finalRightPanelProps.footer.retailFooter.options?.filter(
												x => x?.visible !== false,
											) || []
										}
									/>
								)}
							</div>
						)}
					</Grid>
				</Grid>
			</div>

			<CommonDialogV2
				fullWidth={true}
				open={openSwitch}
				onClose={() => {
					setOpenSwitch(false);
					resetOutlet();
				}}
				sections={{
					header: {
						dynamic: (
							<div className="">
								<div className="dialog-dynamic-content">
									<div className="session">
										<div
											className="title"
											style={{ color: '#ff9800' }}
										>{`Switch Outlet`}</div>
									</div>
								</div>
							</div>
						),
					},
					body: () => (
						<>
							{getAssignedOutletByUser?.length > 2 ? (
								<div className="rm-padding table-wrap sample">
									<List
										className="core-list content-wrap full"
										style={{ paddingBottom: '0px' }}
									>
										<ListItem
											style={{
												boxShadow: 'none',
												padding: '8px 12px 0px 12px',
											}}
										>
											<TextField
												// className="m-b-20"
												id="standard-basic"
												label="Search here..."
												fullWidth
												// value={`${filteredList?.name ?? 0}`}
												onChange={e => {
													handleSearchOutlet(`'"${e?.target?.value}"`, [
														'name',
													]);
													localStorage.setItem(
														'searchFilter',
														JSON.stringify({ value: e.target.value }),
													);
													localStorage.removeItem('searchFilterTable');
												}}
												// fixed
												// search
												// onClose={() => handleSearch('', [])}
											/>
										</ListItem>
									</List>
								</div>
							) : null}
							<ContentWrapper
								style={{
									marginTop: '0px',
									width: 'inherit',
									position: 'initial',
									padding: '8px 0px 8px 0px',
								}}
							>
								<List>
									{filteredListOutlet?.map((outlet, index) => (
										<>
											<ListItem
												key={outlet?.ID}
												style={{
													backgroundColor:
														outlet?.ID === outletID ? '#E5EDFF' : null,
												}}
											>
												<div style={{ paddingRight: '6px' }}>
													{outlet?.logo?.fileURL ? (
														<img
															src={outlet?.logo?.fileURL}
															style={{
																display: 'flex',
																marginTop: '2px',
																marginBottom: '2px',
																marginRight: '4px',
																width: '40px',
																height: '40px',
																justifyItems: 'center',
																borderRadius: '30px',
															}}
															loading="lazy"
														/>
													) : (
														<img
															src={OutletIcon}
															style={{
																display: 'flex',
																marginTop: '7px',
																marginBottom: '8px',
																marginRight: '4px',
																width: '40px',
																height: '25px',
																justifyItems: 'center',
																borderRadius: '30px',
															}}
															loading="lazy"
														/>
													)}
												</div>
												<ListItemText
													primary={
														<span className="xsTitle">{outlet?.name}</span>
													}
													onClick={() => {
														console?.log(outlet, 'what');
														history.push(
															outlet?.isRetail === false
																? `/menu/outlet-app/${outlet?.ID}/table/grid`
																: `/menu/outlet-app/${outlet?.ID}/retail`,
														);
														setOpenSwitch(false);
														localStorage.setItem('latestOutlet', outlet?.ID);
														localStorage.removeItem('searchFilterTable');
														// loadCurrentMealPeriod({
														// 	variables: { outletID: outlet?.ID },
														// });
													}}
												/>
												{outlet?.ID === outletID ? (
													<ListItemSecondaryAction>
														<IconButton edge="end" aria-label="delete">
															<Check color="primary" />
														</IconButton>
													</ListItemSecondaryAction>
												) : null}
											</ListItem>
											<Divider
												// variant="fullWidth"
												style={{ display: 'flex', width: '100%' }}
											/>
										</>
									))}
								</List>
							</ContentWrapper>
						</>
					),
					footer: {
						actions: [
							{
								displayText: 'Close',
								props: {
									onClick: () => {
										setOpenSwitch(false);
										resetOutlet();
									},
									variant: 'contained',
									color: 'primary',
								},
							},
						],
					},
				}}
			/>

			<RetailMenuDialogAdd
				state={state}
				dispatch={dispatch}
				currencyCode={getOutlet[0]?.currencyCode ?? ''}
				attachmentData={attachmentData}
				control={control}
				register={register}
				errors={errors}
				setValue={setValue}
				watch={watch}
				clearErrors={clearErrors}
				unregister={unregister}
				handleSubmit={handleSubmit}
				isDesktop={isDesktop}
				audioInput={audioInput}
				setAudioInput={setAudioInput}
				record={record}
				setRecord={setRecord}
				addOrderItem={AddMenuOrderItem}
				createOrderItemWithOptionsLoading={createOrderItemWithOptionsLoading}
			/>

			<RetailMenuDialogSummary
				state={state}
				dispatch={dispatch}
				audioInput={audioInput}
				setAudioInput={setAudioInput}
				record={record}
				setRecord={setRecord}
				currencyCode={getOutlet[0]?.currencyCode ?? ''}
				control={control}
				register={register}
				errors={errors}
				watch={watch}
				setValue={setValue}
				unregister={unregister}
				clearErrors={clearErrors}
				handleSubmit={handleSubmit}
				isDesktop={isDesktop}
				updateOrderItem={UpdateMenuOrderItem}
				orderItemWithOptionsLoading={updateOrderItemWithOptionsLoading}
			/>

			<RetailPaymentDialog
				state={state}
				dispatch={dispatch}
				handleSubmit={handleSubmit}
				currencyCode={getOutlet[0]?.currencyCode ?? ''}
				total={footerAmount.total}
				register={register}
				setValue={setValue}
				watch={watch}
				control={control}
				reset={reset}
				setSelectedCredit={setSelectedCredit}
				loading1={paymentQuantityLoading}
				getDefaultPayment={getDefaultPayment}
				onSubmit={onSubmit}
				subtotal={footerAmount?.subtotal}
				discount={footerAmount?.discount}
				rounding={footerAmount?.roundingAmount}
				locked={locked}
				setLocked={setLocked}
				setLocked2={setLocked2}
			/>

			{/* 👇 ADD THIS HERE */}
			<VoidRetailODDialog
				state={state}
				dispatch={dispatch}
				register={voidRegister}
				control={voidControl}
				errors={voidErrors}
				watch={voidWatch}
				handleSubmit={voidHandleSubmit}
				getReason={getReason}
				onSubmitVoid={onSubmitVoidOD}
				deleteOrderDiscountLoading={deleteOrderDiscountLoading}
			/>
		</>
	);
};

export default RetailSplitScreen;
